import * as React from "react";
import { useNavigate } from "react-router";
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Container,
  Alert,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useAuth } from "../../util/authenticationProvider";

export default function ControlPanelLogin() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Handle form submit and trigger the AuthContext login flow
   */
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.currentTarget);
    const username = formData.get("username")?.toString() ?? "";
    const password = formData.get("password")?.toString() ?? "";

    try {
      await login(username, password, () => navigate("/cp/overview"));
    } catch (err: any) {
      console.error("Login error:", err);
      setError(
          t("errors.loginFailed") ||
          "Login fehlgeschlagen. Bitte Zugangsdaten prüfen."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
              sx={{
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
          >
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              {t("general.Signin") || "Sign in"}
            </Typography>

            <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{ mt: 1, width: "100%" }}
            >
              <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label={t("general.Username") || "Username"}
                  name="username"
                  autoComplete="username"
                  autoFocus
              />
              <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t("general.Password") || "Password"}
                  type="password"
                  id="password"
                  autoComplete="current-password"
              />

              {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
              )}

              <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2 }}
              >
                {loading
                    ? t("general.Loading") || "Wird geladen..."
                    : t("general.Signin") || "Sign in"}
              </Button>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
  );
}
